// ═══════════════════════════════════════════════════════════════
// DonorDetailView — Campaigns Section
// Path: src/components/donorDetail/CampaignsSection.jsx
//
// Shows which campaigns this donor was a recipient of.
// Fetches from campaign_recipients joined to campaigns table.
//
// Props-only — does not call useApp() directly.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import { Megaphone } from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { campaignService } from "../../services/campaignService";

export const CampaignsSection = ({ member, palette }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!member?.id) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const rows = await campaignService.getCampaignsByMember(member.id);
        if (!cancelled) setCampaigns(rows);
      } catch (err) {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();

    return () => { cancelled = true; };
  }, [member?.id]);

  const fmtDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr + "T12:00:00");
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const statusColor = (status) => {
    if (status === "active") return "#10b981";
    if (status === "closed") return palette.text3;
    return "#f59e0b"; // draft
  };

  if (loading) {
    return (
      <div
        style={{
          padding: 16,
          textAlign: "center",
          color: palette.text3,
          fontSize: FONT_SIZES.xs,
          fontFamily: FONT_FAMILY,
        }}
      >
        Loading campaigns…
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "20px 0" }}>
        <Megaphone
          size={ICON_SIZES.xl}
          style={{ color: palette.text3, marginBottom: 8 }}
        />
        <p
          style={{
            color: palette.text3,
            fontSize: FONT_SIZES.sm,
            margin: 0,
          }}
        >
          No campaigns for this donor yet.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <span style={{ fontSize: FONT_SIZES.sm, color: palette.text2 }}>
        {campaigns.length} campaign{campaigns.length !== 1 ? "s" : ""}
      </span>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          maxHeight: 320,
          overflowY: "auto",
        }}
      >
        {campaigns.map((entry) => {
          const campaign = entry.campaigns;
          if (!campaign) return null;
          return (
            <div
              key={entry.id}
              style={{
                display: "flex",
                gap: 12,
                padding: "12px 14px",
                borderRadius: 8,
                background: palette.inputBg,
                border: `1px solid ${palette.cardBorder}`,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  flexShrink: 0,
                  background: `${statusColor(campaign.status)}12`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Megaphone
                  size={ICON_SIZES.md}
                  style={{ color: statusColor(campaign.status) }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: FONT_SIZES.sm,
                    fontWeight: 600,
                    color: palette.text1,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  {campaign.name}
                </div>
                {campaign.segment_description && (
                  <div
                    style={{
                      fontSize: FONT_SIZES.xs,
                      color: palette.text2,
                      marginTop: 2,
                      lineHeight: 1.3,
                    }}
                  >
                    {campaign.segment_description}
                  </div>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: 2,
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    fontSize: FONT_SIZES.xs,
                    color: palette.text3,
                    whiteSpace: "nowrap",
                  }}
                >
                  {fmtDate(campaign.date_sent)}
                </span>
                <span
                  style={{
                    fontSize: "0.5rem",
                    fontWeight: 600,
                    padding: "1px 6px",
                    borderRadius: 4,
                    background: `${statusColor(campaign.status)}15`,
                    color: statusColor(campaign.status),
                    fontFamily: FONT_FAMILY,
                    textTransform: "capitalize",
                  }}
                >
                  {campaign.status}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
